import type { RouteLocationNormalized } from 'vue-router';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { isOrigemInter } from '@shared/inter/utils/isOrigemInter';
import {
  etapaPendenteDaQualificacao,
  motivoBloqueioDaQuery,
  origemAbaDaQuery,
} from '@/features/credito/domain/qualificacao';
import {
  CREDITO_BLOQUEADO_PATH,
  CREDITO_COMO_FUNCIONA_PATH,
  CREDITO_PATH_PREFIX,
  CREDITO_QUALIFICACAO_INTENCAO_PATH,
  CREDITO_QUALIFICACAO_PATH,
  CREDITO_SUCESSO_PATH,
} from '@/features/credito/routes';
import { useCreditoJourneyStore } from '@/features/credito/stores/useCreditoJourneyStore';
import { isAppInter } from '@/helpers/isAppInter';
import { sessionService, themeService } from '@/services';

const FALLBACK_PATH = '/dividas';

/**
 * Gate da jornada de consignado CLT. Único ponto que lê a flag: nem componente nem guard
 * consultam `appSettings` ou o `sessionService` direto.
 *
 * O rollout progressivo (% da base) é server-side: o `meu-acerto-portal` avalia a feature
 * `feat_credito_clt` no GrowthBook durante o login e publica o resultado no claim `featureFlags`
 * do JWT. Aqui só se lê. O bucketing é sticky por CPF (o atributo `id` do GrowthBook é o mesmo
 * `hash` do token), então quem entrou na fatia não sai dela a cada login.
 */
export function isCreditoCltEnabled(): boolean {
  return (
    // Corte imediato: a flag do GrowthBook só é recarimbada no próximo login (token de 8h), então
    // este `appSettings` continua sendo o kill-switch que vale na hora, para todos.
    String(appSettings.VUE_APP_CREDITO_ENABLED) === 'true' &&
    // Sem `VUE_APP_CREDITO_API_URL` o `baseURL` fica relativo: os requests da jornada cairiam no
    // domínio do portal e o 401 de lá mexe com a sessão. Sem endereço, jornada desligada.
    isCreditoCltFollowUpAvailable() &&
    // Crédito é produto da Acerto: não se oferece dentro da experiência de um parceiro. Fixo no
    // código, e não em `appSettings`, de propósito — ligar para o Inter é decisão de contrato e
    // marca, que precisa passar por revisão, não virar um toggle de infra.
    !isOrigemInter() &&
    sessionService.isFeatureFlagOn(FEATURE_FLAGS.CREDITO_CLT)
  );
}

/**
 * Acompanhar uma proposta existente não participa do rollout de entrada. O backend mantém os
 * endpoints de histórico, assinatura e documentos acessíveis depois que a flag é desligada; o
 * frontend só precisa de uma URL configurada para conseguir consumi-los.
 */
export function isCreditoCltFollowUpAvailable(): boolean {
  return Boolean(String(appSettings.VUE_APP_CREDITO_API_URL ?? '').trim());
}

/**
 * Regra de redirecionamento no router global: com a entrada desligada, bloqueia as rotas que
 * iniciam uma jornada, mas preserva o acompanhamento de propostas existentes.
 */
export function resolveCreditoNavigationRedirect(to: RouteLocationNormalized): string | true {
  if (!to.path.startsWith(CREDITO_PATH_PREFIX)) {
    return true;
  }

  // Antes do acompanhamento: `/credito/sucesso` é o único caminho que não passa por
  // `isCreditoCltEnabled`, então só daqui o bloqueio alcança a jornada inteira.
  if (isOrigemInter(to.query?.tema)) {
    return FALLBACK_PATH;
  }

  if (to.path === CREDITO_SUCESSO_PATH && isCreditoCltFollowUpAvailable()) {
    return true;
  }

  if (isCreditoCltEnabled()) {
    return resolveQualificacaoRedirect(to);
  }

  return FALLBACK_PATH;
}

const ROTAS_COM_PRE_REQUISITO_DE_QUALIFICACAO = new Set([
  CREDITO_QUALIFICACAO_INTENCAO_PATH,
  CREDITO_COMO_FUNCIONA_PATH,
  CREDITO_BLOQUEADO_PATH,
]);

/**
 * As perguntas de qualificação são fricção de UX, não segurança: a barreira de verdade é o gate do
 * backend. A regra fica aqui, num lugar só, para que nenhuma tela da jornada seja alcançável por deep
 * link ou F5 sem as respostas que a liberam (elas ficam só em memória, de propósito).
 */
function resolveQualificacaoRedirect(to: RouteLocationNormalized): string | true {
  if (!ROTAS_COM_PRE_REQUISITO_DE_QUALIFICACAO.has(to.path)) {
    return true;
  }

  const journey = useCreditoJourneyStore();
  const etapaPendente = etapaPendenteDaQualificacao({
    vinculo: journey.qualificacaoVinculo,
    intencao: journey.qualificacaoIntencao,
  });
  const origemAba = origemAbaDaQuery(to.query?.origemAba);
  const voltarPara = (path: string) => `${path}?origemAba=${origemAba}`;

  switch (to.path) {
    case CREDITO_QUALIFICACAO_INTENCAO_PATH:
      return etapaPendente === 'vinculo' ? voltarPara(CREDITO_QUALIFICACAO_PATH) : true;
    case CREDITO_COMO_FUNCIONA_PATH:
      if (etapaPendente === 'vinculo') {
        return voltarPara(CREDITO_QUALIFICACAO_PATH);
      }
      return etapaPendente === 'intencao' ? voltarPara(CREDITO_QUALIFICACAO_INTENCAO_PATH) : true;
    case CREDITO_BLOQUEADO_PATH: {
      const motivo = motivoBloqueioDaQuery(to.query?.motivo);
      if (!motivo) {
        return FALLBACK_PATH;
      }
      // Os demais motivos nascem da simulação, depois da qualificação, e não dependem das respostas.
      const semVinculoSemResposta = motivo === 'sem_vinculo_clt' && journey.qualificacaoVinculo !== 'nao';
      const soOlhandoSemVinculo = motivo === 'so_olhando' && etapaPendente === 'vinculo';
      return semVinculoSemResposta || soOlhandoSemVinculo ? voltarPara(CREDITO_QUALIFICACAO_PATH) : true;
    }
    default:
      return true;
  }
}
